import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Typography, Divider, Avatar, Tooltip, IconButton, AppBar, Toolbar, Collapse
} from '@mui/material';
import {
  DashboardOutlined, PeopleOutlined, MessageOutlined,
  PhoneAndroidOutlined, TrackChangesOutlined, WarningAmberOutlined,
  LogoutOutlined, SmsOutlined, MenuOutlined, SwapHorizOutlined,
  SmsFailedOutlined, VaccinesOutlined, ExpandLess, ExpandMore,
  LocalHospitalOutlined, PregnantWomanOutlined, FavoriteOutlined
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';

const DRAWER_WIDTH = 256;

const MOH = {
  blue:      '#7FB7E6',
  red:       '#EF3232',
  yellow:    '#FDDF03',
  darkNavy:  '#0B1C3D',
  navyMid:   '#112244',
  navyLight: '#1A3060',
  white:     '#FFFFFF',
  textLight: '#B8CCE4',
};

const NAV_GROUPS = [
  {
    id: 'ncd',
    label: 'NCD (Dokotela)',
    color: MOH.blue,
    icon: <LocalHospitalOutlined sx={{ fontSize:18 }}/>,
    items: [
      { label: 'Overview',        path: '/',                icon: <DashboardOutlined/> },
      { label: 'Enrolment',       path: '/enrolment',       icon: <PeopleOutlined/> },
      { label: 'Messages',        path: '/messages',        icon: <MessageOutlined/> },
      { label: 'Telecoms',        path: '/telecoms',        icon: <PhoneAndroidOutlined/> },
      { label: 'Engagement',      path: '/engagement',      icon: <TrackChangesOutlined/> },
      { label: 'Traffic Flow',    path: '/traffic',         icon: <SwapHorizOutlined/> },
      { label: 'Non-Responders',  path: '/non-responders',  icon: <WarningAmberOutlined/> },
      { label: 'Failed Messages', path: '/failed-messages', icon: <SmsFailedOutlined/> },
    ]
  },
  {
    id: 'immunization',
    label: 'Immunization (Make)',
    color: '#CE93D8',
    icon: <VaccinesOutlined sx={{ fontSize:18 }}/>,
    items: [
      { label: 'Immunization Dashboard', path: '/immunization', icon: <VaccinesOutlined/> },
    ]
  },
  {
    id: 'srh',
    label: 'SRH (HIUD)',
    color: '#EF9A9A',
    icon: <FavoriteOutlined sx={{ fontSize:18 }}/>,
    items: [],
    comingSoon: true
  }
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Auto-open group that contains current path
  const activeGroup = NAV_GROUPS.find(g => g.items.some(i => i.path === location.pathname))?.id || 'ncd';
  const [openGroups, setOpenGroups] = useState({ [activeGroup]: true });

  const toggleGroup = (id) => setOpenGroups(prev => ({ ...prev, [id]: !prev[id] }));

  const drawerContent = (
    <Box sx={{ height:'100%', display:'flex', flexDirection:'column', bgcolor: MOH.darkNavy, overflowY:'auto' }}>

      {/* MoH Header Banner */}
      <Box sx={{ bgcolor:'#FFFFFF', px:1.5, py:1,
        borderBottom:`3px solid ${MOH.red}` }}>
        <img src="/moh-header.png" alt="Ministry of Health - Kingdom of Eswatini"
          style={{ width:'100%', maxHeight:68, objectFit:'contain' }}/>
      </Box>

      {/* Flag stripe */}
      <Box sx={{ display:'flex', height:5 }}>
        {[MOH.blue, MOH.red, MOH.yellow, MOH.blue].map((c,i) =>
          <Box key={i} sx={{ flex:1, bgcolor:c }}/>)}
      </Box>

      {/* App title */}
      <Box sx={{ px:2, py:1.5, display:'flex', alignItems:'center', gap:1.5 }}>
        <Box sx={{ width:34, height:34, borderRadius:'8px',
          background:`linear-gradient(135deg, ${MOH.blue}, #3a7abf)`,
          display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
          boxShadow:`0 0 12px rgba(127,183,230,0.25)` }}>
          <SmsOutlined sx={{ fontSize:16, color:'#fff' }}/>
        </Box>
        <Box>
          <Typography sx={{ color:MOH.blue, fontFamily:'"IBM Plex Mono",monospace',
            fontWeight:700, fontSize:'0.78rem', lineHeight:1.2 }}>
            TextWell4Health
          </Typography>
          <Typography sx={{ color:MOH.textLight, fontSize:'0.63rem', lineHeight:1 }}>
            SMS Analytics Platform
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor:`rgba(127,183,230,0.12)` }}/>

      {/* Collapsible Nav Groups */}
      <List sx={{ px:1, pt:0.5, flex:1 }} disablePadding>
        {NAV_GROUPS.map(group => {
          const isOpen    = !!openGroups[group.id];
          const hasActive = group.items.some(i => i.path === location.pathname);

          return (
            <Box key={group.id} mb={0.3}>
              {/* Group Header — clickable to collapse */}
              <ListItemButton onClick={() => toggleGroup(group.id)}
                sx={{ borderRadius:'8px', py:0.8, px:1.5,
                  bgcolor: hasActive ? `rgba(127,183,230,0.08)` : 'transparent',
                  '&:hover':{ bgcolor:`rgba(127,183,230,0.06)` } }}>
                <ListItemIcon sx={{ minWidth:30, color: group.color }}>
                  {group.icon}
                </ListItemIcon>
                <ListItemText primary={group.label}
                  primaryTypographyProps={{
                    fontSize:'0.75rem', fontWeight:700,
                    color: hasActive ? MOH.white : group.color,
                    fontFamily:'"IBM Plex Mono",monospace',
                    letterSpacing:0.3
                  }}/>
                {group.comingSoon ? (
                  <Typography sx={{ fontSize:'0.55rem', color:`rgba(127,183,230,0.4)`,
                    fontStyle:'italic', mr:0.5 }}>soon</Typography>
                ) : (
                  isOpen
                    ? <ExpandLess sx={{ fontSize:16, color:`rgba(127,183,230,0.4)` }}/>
                    : <ExpandMore  sx={{ fontSize:16, color:`rgba(127,183,230,0.4)` }}/>
                )}
              </ListItemButton>

              {/* Group Items */}
              <Collapse in={isOpen && !group.comingSoon} timeout="auto" unmountOnExit>
                <List disablePadding sx={{ pl:1.5 }}>
                  {group.items.map(item => {
                    const active = location.pathname === item.path;
                    return (
                      <ListItem key={item.path} disablePadding sx={{ mb:0.2 }}>
                        <ListItemButton
                          onClick={() => { navigate(item.path); setMobileOpen(false); }}
                          sx={{ borderRadius:'8px', py:0.65, pl:1.5,
                            bgcolor: active ? `rgba(127,183,230,0.12)` : 'transparent',
                            borderLeft: active ? `3px solid ${group.color}` : '3px solid transparent',
                            '&:hover':{ bgcolor:`rgba(127,183,230,0.07)` } }}>
                          <ListItemIcon sx={{ minWidth:28,
                            color: active ? group.color : `rgba(127,183,230,0.4)` }}>
                            {React.cloneElement(item.icon, { sx:{ fontSize:16 } })}
                          </ListItemIcon>
                          <ListItemText primary={item.label}
                            primaryTypographyProps={{
                              fontSize:'0.8rem', fontWeight: active ? 600 : 400,
                              color: active ? MOH.white : MOH.textLight,
                              fontFamily:'"IBM Plex Sans",sans-serif'
                            }}/>
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </List>

      <Divider sx={{ borderColor:`rgba(127,183,230,0.1)` }}/>

      {/* User */}
      <Box sx={{ px:2, py:1.5, display:'flex', alignItems:'center', gap:1.5 }}>
        <Avatar sx={{ width:30, height:30, bgcolor: MOH.navyLight,
          border:`1px solid ${MOH.blue}`, fontSize:'0.75rem', color: MOH.blue }}>
          {user?.name?.charAt(0) || 'A'}
        </Avatar>
        <Box flex={1} minWidth={0}>
          <Typography sx={{ fontSize:'0.78rem', fontWeight:600, color: MOH.white }} noWrap>
            {user?.name || 'Admin'}
          </Typography>
          <Typography sx={{ fontSize:'0.63rem', color: MOH.textLight }} noWrap>
            Ministry of Health
          </Typography>
        </Box>
        <Tooltip title="Logout">
          <IconButton size="small" onClick={logout}
            sx={{ color: MOH.textLight, '&:hover':{ color: MOH.red } }}>
            <LogoutOutlined fontSize="small"/>
          </IconButton>
        </Tooltip>
      </Box>

      {/* Flag accent bottom */}
      <Box sx={{ display:'flex', height:4 }}>
        {[MOH.blue, MOH.red, MOH.yellow, MOH.blue].map((c,i) =>
          <Box key={i} sx={{ flex:1, bgcolor:c }}/>)}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display:'flex', minHeight:'100vh', bgcolor:'background.default' }}>
      {/* Mobile AppBar */}
      <AppBar position="fixed" sx={{ display:{ sm:'none' }, bgcolor: MOH.darkNavy,
        boxShadow:'none', borderBottom:`1px solid rgba(127,183,230,0.15)` }}>
        <Toolbar sx={{ gap:1 }}>
          <IconButton onClick={() => setMobileOpen(true)} sx={{ color: MOH.blue }}>
            <MenuOutlined/>
          </IconButton>
          <img src="/moh-header.png" alt="MoH" style={{ height:34, objectFit:'contain' }}/>
        </Toolbar>
      </AppBar>

      {/* Desktop Drawer */}
      <Drawer variant="permanent" sx={{ width: DRAWER_WIDTH, flexShrink:0,
        display:{ xs:'none', sm:'block' },
        '& .MuiDrawer-paper':{ width: DRAWER_WIDTH, boxSizing:'border-box',
          borderRight:`1px solid rgba(127,183,230,0.1)` } }}>
        {drawerContent}
      </Drawer>

      {/* Mobile Drawer */}
      <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)}
        sx={{ display:{ sm:'none' }, '& .MuiDrawer-paper':{ width: DRAWER_WIDTH } }}>
        {drawerContent}
      </Drawer>

      {/* Main Content */}
      <Box component="main" sx={{ flex:1, minWidth:0, mt:{ xs:7, sm:0 },
        p:{ xs:2, sm:3 }, bgcolor:'#060e1f' }}>

        {/* MoH Top Banner */}
        <Box sx={{ mb:2, p:1.5, borderRadius:1,
          background:`linear-gradient(135deg, ${MOH.darkNavy}, ${MOH.navyMid})`,
          border:`1px solid rgba(127,183,230,0.12)`,
          display:'flex', alignItems:'center', justifyContent:'space-between',
          flexWrap:'wrap', gap:1 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <img src="/moh-header.png" alt="Ministry of Health"
              style={{ height:42, objectFit:'contain' }}/>
            <Box sx={{ display:'flex', gap:0 }}>
              {[MOH.blue, MOH.red, MOH.yellow].map((c,i) =>
                <Box key={i} sx={{ width:4, height:38, bgcolor:c }}/>)}
            </Box>
          </Box>
          <Box textAlign="right">
            <Typography sx={{ color: MOH.textLight, fontSize:'0.7rem' }} display="block">
              TextWell4Health SMS Analytics Platform
            </Typography>
            <Typography sx={{ color:`rgba(127,183,230,0.45)`, fontSize:'0.63rem' }}>
              Kingdom of Eswatini · Ministry of Health
            </Typography>
          </Box>
        </Box>

        {children}
      </Box>
    </Box>
  );
}
